import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import LogRocket from 'logrocket';
import PropTypes from 'prop-types';
import { Button, DownloadIcon, Error, Select, toast, truncateTextWithEllipsis } from '@utilities';
import { exportRawData } from '@api/survey_dashboard';
import { SEARCH_PARAM_KEYS } from '../../../Survey/utilities/constants';

import AskWhyResponses from './components/AskWhyResponses';
import styles from './_index.module.scss';

const AskWhyTabV2 = ({ askWhyInstantSurveys }) => {
  const panelistOptions = useMemo(
    () =>
      (askWhyInstantSurveys || []).map(({ id, title }) => ({
        label: truncateTextWithEllipsis({ length: 40, text: title }),
        value: { id, title },
      })),
    [askWhyInstantSurveys]
  );

  const [searchParams, setSearchParams] = useSearchParams();
  const selectedAskWhySurveyIdParam = searchParams.get(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID);
  const [selectedPanelist, setSelectedPanelist] = useState(() => panelistOptions[0] ?? null);

  useEffect(() => {
    if (!panelistOptions.length) {
      setSelectedPanelist(null);
      if (!selectedAskWhySurveyIdParam) return;
      const params = new URLSearchParams(searchParams);
      params.delete(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID);
      setSearchParams(params, { replace: true });
      return;
    }

    if (selectedAskWhySurveyIdParam) {
      const matchedOption = panelistOptions.find(
        (option) => `${option.value?.id}` === selectedAskWhySurveyIdParam
      );

      if (matchedOption) {
        setSelectedPanelist((prev) =>
          prev?.value?.id === matchedOption.value?.id ? prev : matchedOption
        );
        return;
      }
    }

    const fallback = panelistOptions[0];
    if (!fallback) return;

    setSelectedPanelist((prev) => (prev?.value?.id === fallback.value?.id ? prev : fallback));

    if (fallback.value?.id && fallback.value.id !== selectedAskWhySurveyIdParam) {
      const params = new URLSearchParams(searchParams);
      params.set(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID, fallback.value.id);
      setSearchParams(params, { replace: true });
    }
  }, [panelistOptions, searchParams, selectedAskWhySurveyIdParam, setSearchParams]);

  const handlePanelistChange = useCallback(
    (nextPanelist) => {
      setSelectedPanelist(nextPanelist);
      const params = new URLSearchParams(searchParams);

      if (nextPanelist?.value?.id) {
        params.set(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID, nextPanelist.value.id);
      } else {
        params.delete(SEARCH_PARAM_KEYS.ASK_WHY_SURVEY_ID);
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  useEffect(() => {
    LogRocket.track('AskWhy_TabV2');
  }, []);

  const selectedPanelistSurveyId = selectedPanelist?.value?.id;

  const handleDownloadRawData = useCallback(async () => {
    try {
      await exportRawData({ surveyId: selectedPanelistSurveyId });
    } catch (error) {
      console.error('[AskWhyTabV2] Raw data export failed', error);
      toast('Error occurred while exporting raw data.', { status: 'error' });
    }
  }, [selectedPanelistSurveyId]);

  if (!panelistOptions.length) {
    return <Error status={404} />;
  }

  return (
    <div className={styles['ask-why-tab']}>
      <div className={styles['ask-why-tab-panelists']}>
        <Select
          label="Panelists"
          onChange={handlePanelistChange}
          options={panelistOptions}
          value={selectedPanelist}
        />
        <Button
          icon={<DownloadIcon />}
          isDisabled={!selectedPanelistSurveyId}
          onClick={handleDownloadRawData}
          text="Download Raw Data"
          variant="secondary"
        />
      </div>

      {selectedPanelistSurveyId && (
        <AskWhyResponses
          onDownloadRawData={handleDownloadRawData}
          surveyId={selectedPanelistSurveyId}
        />
      )}
    </div>
  );
};

AskWhyTabV2.propTypes = {
  askWhyInstantSurveys: PropTypes.array,
};

export default AskWhyTabV2;
